import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Card, CardBody } from 'reactstrap';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import { apiPost } from '../../../Utils';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Context/AuthContext';
import StatisticsCards from './StatisticsCards';
import ExpenseTrendChart from './ExpenseTrendChart';
import VehicleStatusChart from './VehicleStatusChart';
import BranchDistributionChart from './BranchDistributionChart';
import WelcomeBanner from '../Default/WelcomeBanner';
import RecentExpenses from './RecentExpenses';
import AlertsWidget from './AlertsWidget';
import './FleetDashboard.scss';


interface DashboardData {
    statistics: {
        vehicles: { total: number; active: number; inactive: number; maintenance: number; sold: number };
        drivers: { total: number; active: number; inactive: number };
        maintenance: { pending: number; overdue: number };
        renewals: { upcoming: number; insurance: number; rc: number; license: number; next_service?: number };
        branches: { total: number; active: number };
    };
    charts: {
        expense_trend: { months: string[]; data: number[] };
        vehicle_status: Array<{ name: string; value: number }>;
        branch_distribution: Array<{ branch_name: string; count: number }>;
    };
    recent_expenses: Array<any>;
    alerts: Array<any>;
}

export default function FleetDashboard() {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        if (!hasPermission('dashboard.view')) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await apiPost('reports/stats', {});
            setDashboardData(data);
        } catch (error: any) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (!hasPermission('dashboard.view')) {
        return (
            <>
                <Breadcrumbs
                    pageTitle="Fleet Dashboard"
                    parent="Dashboard"
                    title="Fleet Management"
                />
                <Container fluid>
                    <Card>
                        <CardBody className="text-center p-5">
                            <i className="fa fa-lock text-secondary mb-3" style={{ fontSize: '3rem' }}></i>
                            <h4>Access Denied</h4>
                            <p className="text-muted">You do not have permission to view the dashboard.</p>
                        </CardBody>
                    </Card>
                </Container>
            </>
        );
    }

    if (loading) {
        return (
            <div className="dashboard-loading">
                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-3 text-muted">Loading dashboard data...</p>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <Container fluid>
                <div className="empty-state">
                    <div className="empty-state-icon bg-light-danger">
                        <i className="fa fa-exclamation-triangle text-danger" style={{ fontSize: '2rem' }}></i>
                    </div>
                    <h6>No Data Available</h6>
                    <p>Unable to load dashboard data. Please try refreshing the page.</p>
                    <button className="btn btn-primary mt-2" onClick={fetchDashboardData}>
                        Retry
                    </button>
                </div>
            </Container>
        );
    }

    return (
        <>
            <Breadcrumbs
                pageTitle="Fleet Dashboard"
                parent="Dashboard"
                title="Fleet Management"
                paragraph="Welcome to your Fleet Management Dashboard"
            />
            <Container className="fleet-dashboard default-dashboard" fluid>
                <Row>
                    {/* Row 1: Statistics Cards */}
                    <StatisticsCards statistics={dashboardData.statistics} />

                    {/* Row 2: Welcome Banner & Expense Trend */}
                    <Col xs={12} sm={12} md={12} lg={4} xl={4} className="mb-4">
                        <WelcomeBanner />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={8} xl={8} className="mb-4">
                        <ExpenseTrendChart data={dashboardData.charts.expense_trend} />
                    </Col>

                    {/* Row 3: Recent Expenses & Alerts */}
                    <Col xs={12} sm={12} md={6} lg={6} xl={6} className="mb-4">
                        <RecentExpenses expenses={dashboardData.recent_expenses} />
                    </Col>
                    <Col xs={12} sm={12} md={6} lg={6} xl={6} className="mb-4">
                        <AlertsWidget alerts={dashboardData.alerts} />
                    </Col>

                    {/* Row 4: Vehicle Status & Branch Distribution */}
                    <Col xs={12} sm={12} md={6} lg={5} xl={5} className="mb-4">
                        <VehicleStatusChart data={dashboardData.charts.vehicle_status} />
                    </Col>
                    <Col xs={12} sm={12} md={6} lg={7} xl={7} className="mb-4">
                        <BranchDistributionChart data={dashboardData.charts.branch_distribution} />
                    </Col>

                </Row>
            </Container>
        </>
    );
}
